import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { getAllCustomers } from "../../../../redux/features/customers/customersService";
import Dropdown from "../../../../components/Dropdown/Dropdown";
import { getAllVehicles } from "../../../../redux/features/vehicle/vehicleService";
import { getAllStaffMembers } from "../../../../redux/features/staff/staffService";
import { addNewOrder } from "../../../../redux/features/orders/ordersService";
import { useNavigate } from "react-router-dom";

const CreateOrder = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const customerDropdownRef = useRef(null);
    const agentDropdownRef = useRef(null);
    const vehicleDropdownRef = useRef(null);

    const { customers } = useSelector((state) => state.customer.allCustomers);
    const { isOrderCreating } = useSelector((state) => state.order.newOrder);
    const { isVehiclesLoading, vehicles } = useSelector((state) => state.vehicle.allVehicles);
    const { staffMembers } = useSelector((state) => state.staff.allStaffMembers);

    const [isFirst, setFirst] = useState(true);
    const [isSecond, setSecond] = useState(false);
    const [isThird, setThird] = useState(false);

    const [formData, setFormData] = useState({});

    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setBtnDisable] = useState(false);
    const [skip, setSkip] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState({
        id: '',
        name: ''
    });
    const [selectedAgent, setSelectedAgent] = useState({
        id: '',
        name: ''
    });
    const [allSelectedVehicles, setAllSelectedVehicles] = useState([]);
    let cumulativeSellingPrice = 0;

    const fetchActiveCustomers = useCallback(() => {
        dispatch(getAllCustomers({
            status: 'Active',
        })).unwrap();
    }, [dispatch]);

    const fetchActiveVehicles = useCallback(() => {
        dispatch(getAllVehicles({
            status: true,
            vehicle_status: 'ON SALE'
        })).unwrap();
    }, [dispatch]);

    const fetchActiveCustomerSpecialists = useCallback(() => {
        dispatch(getAllStaffMembers({
            status: 'Active',
            role_name: 'customer_specialist'
        })).unwrap();
    }, [dispatch]);


    useEffect(() => {
        fetchActiveCustomers();
        fetchActiveVehicles();
        fetchActiveCustomerSpecialists();
    }, [fetchActiveCustomers]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleSelect = (option) => {
        setFormData({ ...formData, customer_id: option.id });
        setErrorMessages({ ...errorMessages, name: "" });
        setSelectedCustomer({
            id: option.id,
            name: option.full_name
        });
    };

    const handleSelectAgent = (option) => {
        setFormData({ ...formData, agent_id: option.id });
        setErrorMessages({ ...errorMessages, agent: "" });
        setSelectedAgent({
            id: option.id,
            name: option.full_name
        });
    };

    const handleRemoveFeature = (featureIndex) => {
        const updatedFeatures = [...allSelectedVehicles];
        updatedFeatures.splice(featureIndex, 1);
        setAllSelectedVehicles(updatedFeatures);
    };

    const handleFeatureVehicleSelect = (feature) => {
        setErrorMessages({ ...errorMessages, vehicle: "" });
        const isDuplicate = allSelectedVehicles.some(
            (existingFeature) => existingFeature.id === feature.id
        );

        if (!isDuplicate) {
            setAllSelectedVehicles([...allSelectedVehicles, feature]);
        }
    };

    // Validate input
    const isValidOrder = () => {
        const errors = {};
        if (!formData.customer_id) {
            errors.name = "Customer is required.";
        }
        if (!formData.agent_id) {
            errors.agent = "Agent is required.";
        }
        if (allSelectedVehicles.length <= 0) {
            errors.vehicle = "Vehicle/s is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewOrder = async () => {
        const vehicle_ids = allSelectedVehicles.map(vehicle => Number(vehicle.id));
        formData.vehicle_ids = vehicle_ids;
        formData.customer_id = +formData.customer_id;
        formData.agent_id = +formData.agent_id;
        formData.total_amount = cumulativeSellingPrice;
        formData.total_amount_after_discount = cumulativeSellingPrice;
        setBtnDisable(true);
        try {
            const response = await dispatch(addNewOrder(formData)).unwrap();
            setBtnDisable(false);
            setFirst(true);
            setSecond(false);
            navigate('/admin/orders');
            showToast('success', response.message);
        }
        catch (error) {
            setBtnDisable(false);
            showToast('error', error.message);
        };
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Order</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        {isFirst &&
                            <div className={styles.vehicleFormGroup}>
                                <div className={`form-input-row`}>
                                    <div className={`form-input-col md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Customer Name<span className={`required-star`}>*</span>
                                            </label>
                                            <Dropdown
                                                ref={customerDropdownRef}
                                                options={customers.map(customer => ({
                                                    ...customer,
                                                    name: `${customer.customer_code} - ${customer.full_name}`
                                                }))}
                                                placeholder="Choose an Option"
                                                onSelect={handleSelect}
                                                value={selectedCustomer.name}
                                            />
                                            {errorMessages.name && (
                                                <p className={`text-danger`}>{errorMessages.name}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={`form-input-row`}>
                                    <div className={`form-input-col md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Vehicle<span className={`required-star`}>*</span>
                                            </label>
                                            <Dropdown
                                                ref={vehicleDropdownRef}
                                                options={vehicles.map(vehicle => ({
                                                    ...vehicle,
                                                    name: `${vehicle.stock_no} - ${vehicle.name}`
                                                }))}
                                                placeholder="Choose an Option"
                                                onSelect={handleFeatureVehicleSelect}
                                            />
                                            {errorMessages.vehicle && (
                                                <p className={`text-danger`}>{errorMessages.vehicle}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={`form-input-row`}>
                                    <div className={`form-input-col md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Customer Specialist<span className={`required-star`}>*</span>
                                            </label>
                                            <Dropdown
                                                ref={agentDropdownRef}
                                                options={staffMembers.map(member => ({
                                                    ...member,
                                                    name: member.full_name
                                                }))}
                                                placeholder="Choose an Option"
                                                onSelect={handleSelectAgent}
                                                value={selectedAgent.name}
                                            />
                                            {errorMessages.agent && (
                                                <p className={`text-danger`}>{errorMessages.agent}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={`form-input-row col-md ${styles.colRow}`}>
                                    {allSelectedVehicles ?
                                        allSelectedVehicles.map((vehicle, index) => (
                                            <div className={styles.vehicleCard} key={vehicle.id}>
                                                <div className={styles.headeRows}>
                                                    <p className={styles.vehicleName}>{vehicle?.name}</p>
                                                    <div className={styles.rightSec}>
                                                        <p className={styles.vehiclePrice}>JPY {vehicle?.vehicle_costs[0]?.selling_price.toFixed(2)}</p>
                                                        <button
                                                            type="button"
                                                            key={index}
                                                            className={styles.featureCard}
                                                            onClick={() => handleRemoveFeature(index)}
                                                        >
                                                            {index < allSelectedVehicles.length && "X"}
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className={styles.vehicleDetSec}>
                                                    <img src={vehicle?.image_url} alt="Thumbnail" className={styles.thumbImg} />
                                                    <div className={styles.vehicleDetails}>
                                                        <div className={styles.colSection}>
                                                            <div className={styles.detailsRows}>
                                                                <div className={styles.subSec}>
                                                                    <p className={styles.title}>Stock (Ref#)</p>
                                                                    <p className={styles.titleValue}>{vehicle?.stock_no}</p>
                                                                </div>
                                                            </div>
                                                            <div className={styles.detailsRows}>
                                                                <div className={styles.subSec}>
                                                                    <p className={styles.title}>Chassis No</p>
                                                                    <p className={styles.titleValue}>{vehicle?.chassis_no}</p>
                                                                </div>
                                                            </div>
                                                            <div className={styles.detailsRows}>
                                                                <div className={styles.subSec}>
                                                                    <p className={styles.title}>Make</p>
                                                                    <p className={styles.titleValue}>{vehicle?.make.name} cc</p>
                                                                </div>
                                                            </div>
                                                            <div className={styles.detailsRows}>
                                                                <div className={styles.subSec}>
                                                                    <p className={styles.title}>Model</p>
                                                                    <p className={styles.titleValue}>{vehicle?.model.name}</p>
                                                                </div>
                                                            </div>
                                                            <div className={styles.detailsRows}>
                                                                <div className={styles.subSec}>
                                                                    <p className={styles.title}>Model Code</p>
                                                                    <p className={styles.titleValue}>{vehicle?.model_code?.name}</p>
                                                                </div>
                                                            </div>
                                                            <div className={styles.detailsRows}>
                                                                <div className={styles.subSec}>
                                                                    <p className={styles.title}>Lot Nm</p>
                                                                    <p className={styles.titleValue}>{vehicle?.lot_nm}</p>
                                                                </div>
                                                            </div>
                                                            <div className={styles.detailsRows}>
                                                                <div className={styles.subSec}>
                                                                    <p className={styles.title}>Auction House</p>
                                                                    <p className={styles.titleValue}>{vehicle?.auction_house}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                        : ""}{" "}
                                </div>
                                <button
                                    className={`btn-primary`}
                                    type='button'
                                    disabled={disabledBtn}
                                    onClick={() => {
                                        if (isValidOrder()) {
                                            setFirst(false);
                                            setSecond(true);
                                        }
                                    }}
                                >
                                    Continue
                                </button>
                            </div>
                        }
                        {isSecond &&
                            <div className={styles.vehicleFormGroup}>
                                <div className={`form-input-row`}>
                                    <div className={`table-container`}>
                                        <table className={styles.table}>
                                            <thead className={styles.thead}>
                                                <tr>
                                                    <th className={styles.theadD}>Year</th>
                                                    <th className={styles.theadD}>Chassis</th>
                                                    <th className={styles.theadD}>Lot NM</th>
                                                    <th className={styles.theadD}>Auction House</th>
                                                    <th className={styles.theadD}>Buying Price</th>
                                                    <th className={styles.theadD}>Auction Fee</th>
                                                    <th className={styles.theadD}>Local Transport</th>
                                                    <th className={styles.theadD}>EC Cost</th>
                                                    <th className={styles.theadD}>Repairing Cost</th>
                                                    <th className={styles.theadD}>FOB Cost</th>
                                                    <th className={styles.theadD}>Freight</th>
                                                    <th className={styles.theadD}>CNF Cost</th>
                                                    <th className={styles.theadD}>J and A Charge</th>
                                                    <th className={styles.theadD}>Selling Price</th>
                                                    <th className={styles.theadD}>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className={styles.tbody}>
                                                {allSelectedVehicles.map((vehicle) => {
                                                    const sellingPrice = vehicle.vehicle_costs[0]?.selling_price || 0;
                                                    cumulativeSellingPrice += sellingPrice; // Add current selling_price to cumulative total

                                                    return (
                                                        <tr key={vehicle.id}>
                                                            <td className={styles.tableCell}>{vehicle.reg_year}</td>
                                                            <td className={styles.tableCell}>{vehicle.chassis_no}</td>
                                                            <td className={styles.tableCell}>{vehicle.lot_nm}</td>
                                                            <td className={styles.tableCell}>{vehicle.auction_house}</td>

                                                            {/** Extract vehicle costs safely */}
                                                            {(() => {
                                                                const costs = vehicle.vehicle_costs[0] || {};
                                                                return (
                                                                    <>
                                                                        <td className={styles.tableCellValue}>{costs.buying_price?.toFixed(2) || parseFloat(0).toFixed(2)}</td>
                                                                        <td className={styles.tableCellValue}>{costs.auction_fee?.toFixed(2) || parseFloat(0).toFixed(2)}</td>
                                                                        <td className={styles.tableCellValue}>{costs.local_transport?.toFixed(2) || parseFloat(0).toFixed(2)}</td>
                                                                        <td className={styles.tableCellValue}>{costs.ec_cost?.toFixed(2) || parseFloat(0).toFixed(2)}</td>
                                                                        <td className={styles.tableCellValue}>{costs.repairing_cost?.toFixed(2) || parseFloat(0).toFixed(2)}</td>
                                                                        <td className={styles.tableCellValue}>{costs.fob_cost?.toFixed(2) || parseFloat(0).toFixed(2)}</td>
                                                                        <td className={styles.tableCellValue}>{costs.freight?.toFixed(2) || parseFloat(0).toFixed(2)}</td>
                                                                        <td className={styles.tableCellValue}>{costs.cnf_cost?.toFixed(2) || parseFloat(0).toFixed(2)}</td>
                                                                        <td className={styles.tableCellValue}>{costs.j_and_a_charge?.toFixed(2) || parseFloat(0).toFixed(2)}</td>
                                                                        <td className={styles.tableCellValue}>{costs.selling_price?.toFixed(2) || parseFloat(0).toFixed(2)}</td>

                                                                        {/** Display Cumulative Selling Price */}
                                                                        <td className={styles.tableCellValue}><strong>{cumulativeSellingPrice?.toFixed(2)}</strong></td>
                                                                    </>
                                                                );
                                                            })()}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                <button
                                    className={`btn-primary`}
                                    type='button'
                                    onClick={() => { setFirst(true); setSecond(false); }}
                                >
                                    Previous
                                </button>
                                <button
                                    className={`btn-primary ${styles.submitBtn}`}
                                    type='button'
                                    disabled={disabledBtn}
                                    onClick={() => { insertNewOrder(); setFirst(false); setSecond(true); }}
                                >
                                    {isOrderCreating ? <BeatLoader staffMember='#ffffff' /> : `Create`}
                                </button>
                            </div>
                        }
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateOrder;
